import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileText, Building2, Clock, AlertCircle, History, Bookmark, X, Trash2 } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { api } from '@/services/auth';
import { SavedSearchesDialog } from '@/components/ui/SavedSearchesDialog';
import { useSearchHistory } from '@/hooks/useSearchHistory';
import { useSavedSearches } from '@/hooks/useSavedSearches';

interface CaseResult {
  type: 'case';
  id: string;
  code: string;
  title: string | null;
  domain: string;
  status: string | null;
  client: {
    id: string;
    name: string;
  } | null;
}

interface ClientResult {
  type: 'client';
  id: string;
  name: string;
  document: string | null;
  email: string | null;
}

/** Public INPI base hit — id/code is the cod_pedido used by /patentes/meus/:patentId. */
interface PatentResult {
  type: 'patent';
  id: string;
  code: string;
  title: string | null;
}

type SearchResult = CaseResult | ClientResult | PatentResult;

interface SearchResponse {
  query: string;
  results: {
    cases: CaseResult[];
    clients: ClientResult[];
    patents: PatentResult[];
  };
}

const MIN_QUERY_LENGTH = 2;

export function GlobalSearch() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [errored, setErrored] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Search history + saved searches (localStorage-backed, see hooks for contract).
  const { entries: history, add: addHistory, remove: removeHistory, clear: clearHistory } = useSearchHistory();
  const { saved, save: saveSearch, remove: removeSaved } = useSavedSearches();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const q = query.trim();

    if (q.length < MIN_QUERY_LENGTH) {
      setResults(null);
      setErrored(false);
      setLoading(false);
      return;
    }

    // Spinner during the debounce window (avoids flashing an error state
    // before the first request fires).
    setLoading(true);
    setErrored(false);

    // Stale-response guard: debounce is NOT cancellation. Without this flag,
    // a slow response for an earlier keystroke would overwrite results for
    // the current one AND pollute the history with a superseded query.
    let stale = false;

    const searchTimer = setTimeout(async () => {
      try {
        const response = await api.get<SearchResponse>(`/v2/search/internal`, {
          params: { q },
        });
        if (stale) return;
        setResults(response.data);
        // Record only executed (server-answered) searches — typing alone
        // must not pollute the history.
        addHistory(q);
      } catch (error) {
        if (stale) return;
        console.error('Erro ao buscar:', error);
        setResults(null);
        setErrored(true);
      } finally {
        if (!stale) setLoading(false);
      }
    }, 300);

    return () => {
      stale = true;
      clearTimeout(searchTimer);
    };
  }, [query, addHistory]);

  // Reset on close so reopening lands on the idle state instead of a stale
  // search (selecting a result resets via handleSelect before navigating).
  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery('');
      setResults(null);
      setErrored(false);
      setLoading(false);
    }
  }, []);

  const reset = useCallback(() => {
    setOpen(false);
    setQuery('');
    setResults(null);
    setErrored(false);
  }, []);

  const handleSelect = (result: SearchResult) => {
    reset();
    if (result.type === 'case') {
      navigate(`/processo/${result.id}`);
    } else if (result.type === 'patent') {
      // Public INPI patent: patentId IS the cod_pedido — the detail page
      // fetches GET /monitoring/patents/:num with it directly.
      navigate(`/patentes/meus/${result.code}`);
    } else {
      navigate(`/comercial/clientes/${result.id}`);
    }
  };

  const handleHistoryClick = useCallback((q: string) => {
    setQuery(q);
  }, []);

  const handleSavedClick = useCallback((q: string) => {
    setQuery(q);
    setOpen(true);
  }, []);

  const getStatusColor = (status: string | null) => {
    switch (status?.toLowerCase()) {
      case 'ativo':
        return 'bg-green-500/10 text-green-500';
      case 'arquivado':
        return 'bg-slate-500/10 text-slate-500';
      case 'cancelado':
        return 'bg-red-500/10 text-red-500';
      case 'concluido':
        return 'bg-blue-500/10 text-blue-500';
      default:
        return 'bg-gray-500/10 text-gray-500';
    }
  };

  const getDomainLabel = (domain: string) => {
    switch (domain) {
      case 'PATENTES':
        return 'Patentes';
      case 'MARCAS':
        return 'Marcas';
      case 'DI':
        return 'DI';
      case 'INTERNACIONAL':
        return 'Internacional';
      default:
        return domain;
    }
  };

  const hasQuery = query.trim().length >= MIN_QUERY_LENGTH;

  // Guard for older backend responses without the patents array.
  const patentResults = results?.results.patents ?? [];

  // Inline remove affordance inside a cmdk item. stopPropagation prevents
  // the row's onSelect from firing on click; Enter/Space are handled
  // explicitly because the span is focusable.
  const renderRemoveButton = (label: string, onRemove: () => void) => (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      className="ml-2 rounded p-1 text-slate-300 opacity-0 transition-opacity hover:bg-slate-100 hover:text-red-500 focus:opacity-100 group-data-[selected=true]:opacity-100"
      onClick={(e) => {
        e.stopPropagation();
        onRemove();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onRemove();
        }
      }}
    >
      <X className="h-3.5 w-3.5" />
    </span>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => handleOpenChange(true)}
        className="flex h-8 w-48 items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 text-sm text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-600"
        aria-label="Abrir busca global"
      >
        <Search className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate text-[11px]">Buscar...</span>
        <kbd className="ml-auto hidden shrink-0 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-400 lg:inline">
          ⌘K
        </kbd>
      </button>

      <CommandDialog
        open={open}
        onOpenChange={handleOpenChange}
        commandProps={{ shouldFilter: false }}
      >
        <CommandInput
          placeholder="Buscar processos, patentes (INPI) ou clientes..."
          value={query}
          onValueChange={setQuery}
        />
        <CommandList>
          {!loading && !hasQuery && (
            <>
              {saved.length > 0 && (
                <CommandGroup heading="Buscas Salvas">
                  {saved.map((entry) => (
                    <CommandItem
                      key={entry.id}
                      value={`saved-${entry.id}`}
                      onSelect={() => handleSavedClick(entry.query)}
                      className="group cursor-pointer"
                    >
                      <Bookmark className="mr-2 h-4 w-4 shrink-0 text-blue-500" />
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm">{entry.name}</span>
                        <span className="truncate text-xs text-slate-400">{entry.query}</span>
                      </div>
                      {renderRemoveButton(`Remover busca salva ${entry.name}`, () => removeSaved(entry.id))}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {history.length > 0 && (
                <CommandGroup heading="Buscas Recentes">
                  {history.map((entry) => (
                    <CommandItem
                      key={entry.q}
                      value={`history-${entry.q}`}
                      onSelect={() => handleHistoryClick(entry.q)}
                      className="group cursor-pointer"
                    >
                      <History className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
                      <span className="flex-1 truncate text-sm">{entry.q}</span>
                      {renderRemoveButton(`Remover do histórico: ${entry.q}`, () => removeHistory(entry.q))}
                    </CommandItem>
                  ))}
                  <CommandItem
                    value="limpar-historico"
                    onSelect={clearHistory}
                    className="cursor-pointer text-xs text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Limpar histórico
                  </CommandItem>
                </CommandGroup>
              )}

              {history.length === 0 && saved.length === 0 && (
                <CommandEmpty>
                  <div className="flex flex-col items-center py-6">
                    <Search className="mb-2 h-8 w-8 text-slate-400" />
                    <p className="text-sm text-slate-400">
                      Digite pelo menos 2 caracteres para buscar
                    </p>
                  </div>
                </CommandEmpty>
              )}
            </>
          )}

          {loading && (
            <div className="py-6 text-center text-sm text-slate-400">
              <Clock className="mx-auto mb-2 h-6 w-6 animate-spin" />
              Buscando...
            </div>
          )}

          {!loading && hasQuery && errored && (
            <CommandEmpty>
              <div className="flex flex-col items-center py-6">
                <AlertCircle className="mb-2 h-8 w-8 text-slate-400" />
                <p className="text-sm text-slate-400">
                  Erro ao realizar busca
                </p>
              </div>
            </CommandEmpty>
          )}

          {!loading && hasQuery && !errored && results && (
            <>
              {patentResults.length > 0 && (
                <CommandGroup heading="Patentes (INPI)">
                  {patentResults.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={`patent-${result.id}`}
                      onSelect={() => handleSelect(result)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-1 items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-indigo-500/10">
                          <FileText className="h-4 w-4 text-indigo-500" />
                        </div>
                        <div className="flex min-w-0 flex-col gap-1">
                          <span className="text-sm font-semibold">{result.code}</span>
                          <p className="truncate text-xs text-slate-400">
                            {result.title || 'Sem título'}
                          </p>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              {results.results.cases.length > 0 && (
                <>
                  {patentResults.length > 0 && <CommandSeparator />}
                  <CommandGroup heading="Processos">
                  {results.results.cases.map((result) => (
                    <CommandItem
                      key={result.id}
                      value={`case-${result.id}`}
                      onSelect={() => handleSelect(result)}
                      className="cursor-pointer"
                    >
                      <div className="flex flex-1 items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-blue-500/10">
                          <FileText className="h-4 w-4 text-blue-500" />
                        </div>
                        <div className="flex min-w-0 flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold">{result.code}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {getDomainLabel(result.domain)}
                            </Badge>
                            {result.status && (
                              <Badge className={`text-[10px] ${getStatusColor(result.status)}`}>
                                {result.status}
                              </Badge>
                            )}
                          </div>
                          <p className="truncate text-xs text-slate-400">
                            {result.title || 'Sem título'}
                          </p>
                          {result.client && (
                            <p className="text-xs text-slate-400">
                              Cliente: {result.client.name}
                            </p>
                          )}
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                  </CommandGroup>
                </>
              )}

              {results.results.clients.length > 0 && (
                <>
                  <CommandSeparator />
                  <CommandGroup heading="Clientes">
                    {results.results.clients.map((result) => (
                      <CommandItem
                        key={result.id}
                        value={`client-${result.id}`}
                        onSelect={() => handleSelect(result)}
                        className="cursor-pointer"
                      >
                        <div className="flex flex-1 items-start gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-emerald-500/10">
                            <Building2 className="h-4 w-4 text-emerald-500" />
                          </div>
                          <div className="flex min-w-0 flex-col gap-1">
                            <span className="text-sm font-semibold">{result.name}</span>
                            {result.document && (
                              <p className="text-xs text-slate-400">
                                {result.document}
                              </p>
                            )}
                            {result.email && (
                              <p className="truncate text-xs text-slate-400">
                                {result.email}
                              </p>
                            )}
                          </div>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}

              {results.results.cases.length === 0 && results.results.clients.length === 0 && patentResults.length === 0 && (
                <CommandEmpty>
                  <div className="flex flex-col items-center py-6">
                    <Search className="mb-2 h-8 w-8 text-slate-400" />
                    <p className="text-sm text-slate-400">
                      Nenhum resultado encontrado para "{query.trim()}"
                    </p>
                  </div>
                </CommandEmpty>
              )}

              <CommandItem
                value="salvar-busca"
                onSelect={() => setDialogOpen(true)}
                className="cursor-pointer border-t text-xs text-slate-500 hover:text-slate-800"
              >
                <Bookmark className="mr-2 h-3.5 w-3.5" />
                Salvar esta busca…
              </CommandItem>
            </>
          )}
        </CommandList>
      </CommandDialog>

      <SavedSearchesDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        currentQuery={query}
        saved={saved}
        onSave={saveSearch}
        onRemove={removeSaved}
        onExecute={handleSavedClick}
      />
    </>
  );
}
